/**
 * useForecastExport Hook
 *
 * Provides a mutation-style hook for triggering forecast data exports.
 * Returns a blob that is automatically downloaded as a file.
 */

import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { forecastsApi } from "@/api";
import type { ForecastExportRequest, ExportFormat } from "@/api/types";

export interface UseForecastExportReturn {
  exportForecasts: (
    request: ForecastExportRequest,
    options?: { onSuccess?: () => void },
  ) => void;
  isExporting: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Trigger a forecast data export (CSV or Excel) and auto-download the file.
 */
export function useForecastExport(): UseForecastExportReturn {
  const mutation = useMutation({
    mutationFn: async (request: ForecastExportRequest) => {
      const blob = await forecastsApi.exportForecasts(request);
      return { blob, format: request.format };
    },
    onSuccess: ({ blob, format }) => {
      triggerDownload(blob, format);
    },
  });

  const exportForecasts = useCallback(
    (
      request: ForecastExportRequest,
      options?: { onSuccess?: () => void },
    ) => {
      mutation.mutate(request, {
        onSuccess: () => {
          options?.onSuccess?.();
        },
      });
    },
    [mutation],
  );

  return {
    exportForecasts,
    isExporting: mutation.isPending,
    error: mutation.error as Error | null,
    reset: mutation.reset,
  };
}

// ============================================================================
// Helpers
// ============================================================================

function getFileExtension(format: ExportFormat): string {
  return format === "excel" ? "xlsx" : "csv";
}

function triggerDownload(blob: Blob, format: ExportFormat) {
  const ext = getFileExtension(format);
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
  const filename = `forecast-export-${timestamp}.${ext}`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  // Clean up
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
